import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePaginatedQuery, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";
import { type UrlRule } from "@/components/AddUrlModal";
import { useAppCache } from "./useAppCache";
import { useMemo, useEffect, useRef } from "react";

export interface AppInfo {
  bundleId: string;
  appName: string;
  iconUrl?: string;
  trackId?: number | null;
  id?: string;
}

export interface NetworkRule {
  id: string;
  name: string;
  description?: string;
  type: "General" | "App Specific";
  category: "system-wide" | "app-specific";
  bundleId?: string;
  appName?: string;
  apps: AppInfo[];
  lastModified: string;
  urls: string[];
  ruleType: "webkit" | "socket";
  function?: boolean;
  scope: {
    users: number;
    devices: number;
    groups: string[];
  };
  secondaryBundleId?: string;
  useSecondaryBundleId?: boolean;
  parentApp?: {
    id: string;
    name: string;
    iconUrl?: string;
    bundleId: string;
  };
  isParentAppLoading?: boolean;
}

export const useNetworkRules = () => {
  const rulesData = usePaginatedQuery(
    api.rules.list.all,
    {
      paginationOpts: {
        cursor: null,
        numItems: 50
      }
    },
    { initialNumItems: 50 }
  );
  
  const { getApp, getCachedApp, triggerFetch } = useAppCache();
  const triggeredAppsRef = useRef<Set<string>>(new Set());
  
  const isConvexLoading = rulesData.status === "LoadingFirstPage";
  
  useEffect(() => {
    if (rulesData.results) {
      const parentAppIds = rulesData.results
        .filter((rule: any) => rule.parent)
        .map((rule: any) => rule.parent);
      
      const newAppIds = parentAppIds.filter(id => !triggeredAppsRef.current.has(id));
      
      if (newAppIds.length > 0) {
        console.log('Triggering fetch for new parent apps:', newAppIds);
        newAppIds.forEach(id => triggeredAppsRef.current.add(id));
        triggerFetch(newAppIds);
      }
    }
  }, [rulesData.results, triggerFetch]);
  
  const transformedRules = useMemo(() => {
    if (!rulesData.results) {
      return [];
    }

    console.log('Processing live Convex rules data:', rulesData.results.length, 'rules');
    
    const transformed: NetworkRule[] = rulesData.results.map((rule: any) => {
      // Debug logging to inspect parent field
      console.log('Raw rule data:', {
        id: rule._id,
        name: rule.name,
        parent: rule.parent,
        hasParent: !!rule.parent,
        fullRule: rule
      });
      
      const updatedAt = new Date(rule.lastCheck || rule._creationTime || Date.now());
      
      // Format as exact date and time
      const lastModified = updatedAt.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      // Check if parent is a valid string ID
      const isAppSpecific = rule.parent && typeof rule.parent === 'string';
      const type = isAppSpecific ? "App Specific" : "General";
      const category = isAppSpecific ? "app-specific" : "system-wide";

      let parentApp = undefined;
      let apps: AppInfo[] = [];
      let isParentAppLoading = false;
      
      if (rule.parent) {
        const parentAppData = getApp(rule.parent);
        const cached = getCachedApp(rule.parent);
        
        isParentAppLoading = cached?.loading || false;
        
        if (parentAppData) {
          parentApp = {
            id: parentAppData.id,
            name: parentAppData.name,
            iconUrl: parentAppData.iconUrl,
            bundleId: parentAppData.bundleId || ''
          };
          
          if (parentAppData.name !== 'Loading...' && parentAppData.bundleId) {
            apps = [{
              bundleId: parentAppData.bundleId,
              appName: parentAppData.name,
              iconUrl: parentAppData.iconUrl,
              trackId: null,
              id: parentAppData.id
            }];
          }
        }
      }

      const secondaryBundleIdData = localStorage.getItem(`secondaryBundleId_${rule._id}`);
      let secondaryBundleId = undefined;
      let useSecondaryBundleId = false;
      
      if (secondaryBundleIdData) {
        try {
          const parsed = JSON.parse(secondaryBundleIdData);
          secondaryBundleId = parsed.secondaryBundleId;
          useSecondaryBundleId = parsed.useSecondaryBundleId || false;
        } catch (e) {
          console.warn('Failed to parse secondary bundle ID data for rule:', rule._id);
        }
      }

      return {
        id: rule._id,
        name: rule.name || 'Unnamed Rule',
        description: rule.description,
        type,
        category,
        bundleId: rule.bundleId || '',
        appName: rule.name || 'Unnamed Rule',
        apps,
        lastModified,
        urls: rule.urls || [],
        ruleType: rule.type || 'webkit',
        function: rule.function || false,
        scope: {
          users: 0,
          devices: 0,
          groups: []
        },
        secondaryBundleId,
        useSecondaryBundleId,
        parentApp,
        isParentAppLoading
      };
    });

    console.log('Live transformed network rules:', transformed.length, 'rules processed');
    
    return transformed;
  }, [rulesData.results, getApp, getCachedApp]);
  
  return {
    data: transformedRules,
    isLoading: isConvexLoading,
    error: null,
    meta: {
      isConvexLoading
    }
  };
};

export const useUpdateNetworkRule = () => {
  const queryClient = useQueryClient();
  const updateRule = useConvexMutation(api.rules.update.rule);

  return useMutation({
    mutationFn: async ({ ruleId, updates }: { ruleId: string; updates: any }) => {
      console.log('Updating network rule:', { ruleId, updates });
      
      const updateData: any = {
        rule: ruleId as any,
      };

      if (updates.name) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.urls) updateData.urls = updates.urls;
      if (updates.bundle_id) updateData.bundleId = updates.bundle_id;

      console.log('Final update data sent to API:', updateData);

      const result = await updateRule(updateData);
      console.log('Network rule updated successfully:', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['network-rules'] });
    }
  });
};

export const useCreateNetworkRule = () => {
  const queryClient = useQueryClient();
  const createRule = useConvexMutation(api.rules.add.rule);

  return useMutation({
    mutationFn: async (ruleData: {
      name: string;
      description?: string;
      type: "General" | "App Specific";
      apps: Array<{
        bundleId: string;
        appName: string;
        iconUrl?: string;
        trackId?: number | null;
        id?: string;
      }>;
      is_app_specific: boolean;
      bundle_id?: string;
      urls?: string[];
      ruleType?: "webkit" | "socket";
      secondary_bundle_id?: string;
      use_secondary_bundle_id?: boolean;
      parent_app_id?: string;
    }) => {
      try {
        console.log('Creating network rule:', ruleData);
        
        const urls = ruleData.urls || [];

        let finalBundleId = ruleData.bundle_id || ruleData.apps[0]?.bundleId || '';
        
        if (ruleData.use_secondary_bundle_id && ruleData.secondary_bundle_id) {
          finalBundleId = ruleData.secondary_bundle_id;
          console.log('Alternative Bundle ID overriding main bundle ID:', ruleData.secondary_bundle_id);
        }

        const createData: any = {
          name: ruleData.name,
          description: ruleData.description || '',
          bundleId: finalBundleId,
          urls
        };

        if (ruleData.is_app_specific && ruleData.parent_app_id) {
          createData.parent = ruleData.parent_app_id as any;
        }

        console.log('Final create data sent to API:', createData);

        const result = await createRule(createData);
        console.log('Network rule created successfully:', result);
        
        if (ruleData.use_secondary_bundle_id && result) {
          const ruleId = typeof result === 'string' ? result : result._id;
          if (ruleId) {
            localStorage.setItem(`secondaryBundleId_${ruleId}`, JSON.stringify({
              secondaryBundleId: ruleData.secondary_bundle_id,
              useSecondaryBundleId: true
            }));
          }
        }
        
        if (result === null) {
          return { success: true };
        }
        
        return result;
      } catch (error) {
        console.error('Error creating network rule:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['network-rules'] });
    }
  });
};

export const useUpdateBundleId = () => {
  const updateRule = useUpdateNetworkRule();
  
  return {
    ...updateRule,
    mutateAsync: async ({ ruleId, bundleId }: { ruleId: string; bundleId: string }) => {
      return updateRule.mutateAsync({
        ruleId,
        updates: { bundle_id: bundleId }
      });
    }
  };
};
